import type { ReactNode } from "react";

import EditableTextCell from "@/components/common/EditableTextCell";
import LinkCell from "@/components/common/LinkCell";

/**
 * 詳細画面の「項目名＋値」1 行。
 *
 * 値は一覧のセルと同じ [[EditableTextCell]] で見せる。一覧でも詳細でも
 * 「青字＋下線の値をクリックすると編集ダイアログが開く」という操作を揃えるため。
 *
 * `linkTo` を渡すと**編集ではなく別の画面へ移る値**になる（案件から起票元の
 * 見積へ、など）。見た目は編集できる値と同じ青字＋下線にしてある。どちらも
 * 「押すと何かが起きる値」で、押した先が違うだけだから。
 *
 * `control` を渡すと、値の代わりにその部品を置く（セット品のスイッチなど）。
 * **一覧の同じ項目と操作を揃えるため**のもので、一覧でスイッチなら詳細でも
 * スイッチにする。
 */
export default function DetailField({
  label,
  value,
  canWrite,
  linkTo,
  control,
  onEdit,
}: {
  label: string;
  value: string | null | undefined;
  canWrite: boolean;
  /** 値を押したときに移る先。渡すと編集ではなくリンクになる。 */
  linkTo?: string;
  /** 値の代わりに置く部品（スイッチなど）。渡すと value は使わない。 */
  control?: ReactNode;
  onEdit: () => void;
}) {
  const text = value?.trim() ? value.trim() : null;
  return (
    <div className="flex items-start gap-3 border-b border-default-100 py-2">
      <span className="w-28 shrink-0 pt-0.5 text-sm text-default-500">{label}</span>
      <div className="min-w-0 flex-1 text-sm">
        {control ? (
          control
        ) : linkTo && text ? (
          <LinkCell to={linkTo} label={`${label} ${text} を開く`}>
            {text}
          </LinkCell>
        ) : (
          <EditableTextCell
            label={label}
            value={value}
            canWrite={canWrite}
            onEdit={onEdit}
          />
        )}
      </div>
    </div>
  );
}
